import {
  useContext,
  ReactElement,
  ReactNode,
} from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import Box from '@mui/material/Box';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Chip, { ChipProps } from '@mui/material/Chip';
import DoneIcon from '@mui/icons-material/Done';
import { INOVICE_ITEM_TYPES } from '#common/constants';

import BarcodeInputBox from '#components/BarcodeInputBox';
import {
  DATE_FORMAT_STRING,
} from '#common/utils';
import {
  invoiceValidations,
} from '#common/validation';
import useForm from '#utils/use-form';
import { FeedbackUIContext } from '#context/feedback-ui-context';

interface InvoiceItem {
  type: string;
  value: string;
}

interface Invoice {
  invoiceDate?: string | Date;
  items: InvoiceItem[];
  [key: string]: unknown;
}

interface InvoiceFormValues {
  invoiceDate: string | Date;
  items: InvoiceItem[];
}

interface Pkg {
  barcodeItems?: string[];
  [key: string]: unknown;
}

interface AdditionalCharge {
  _id: string;
  name: string;
  [key: string]: unknown;
}

interface InvoicePanelProps {
  invoice?: Invoice | undefined;
  invoices: Invoice[];
  additionalCharges: AdditionalCharge[];
  packages: Pkg[];
  onSaveInvoice: (values: InvoiceFormValues) => void;
}

const buildInitialState = (invoice?: Invoice): Record<string, unknown> => {
  return (invoice as unknown as Record<string, unknown>) || {
    invoiceDate: new Date(),
    items: [],
  };
};

export default function InvoicePanel(props: InvoicePanelProps) {
  const {
    invoice,
    invoices,
    additionalCharges,
    packages,
    onSaveInvoice,
  } = props;

  const {
    hideModal,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    isValid,
    errors,
    errorMessages,
    handleChange,
    handleSubmit,
    addSubmitListener,
  } = useForm({
    initialState: buildInitialState(invoice),
    validations: invoiceValidations as Parameters<typeof useForm>[0]['validations'],
  });

  const formValues = values as unknown as InvoiceFormValues;

  const handleSaveInvoice = (submitValues: Record<string, unknown>, valid: boolean) => {
    if (!valid) {
      return;
    }

    hideModal();
    onSaveInvoice(submitValues as unknown as InvoiceFormValues);
  };
  addSubmitListener(handleSaveInvoice);

  const toggleInvoiceItem = (invoiceItem: InvoiceItem, toggle: boolean) => {
    const selected = formValues.items.some(
      ({ value }) => invoiceItem.value === value,
    );

    if (selected && toggle) {
      handleChange(null, {
        name: 'items',
        value: formValues.items.filter(({ value }) => value !== invoiceItem.value),
      });
    } else if (!selected) {
      handleChange(null, {
        name: 'items',
        value: [
          invoiceItem,
          ...formValues.items,
        ],
      });
    }
  };

  return (
    <>
      <FormControl sx={{ mb: 5, width: '100%' }}>
        <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
          >
            <DesktopDatePicker
              label="Invoice Date"
              name="invoiceDate"
              format={DATE_FORMAT_STRING}
              value={new Date(formValues.invoiceDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
              sx={{ minWidth: 70 }}
              onChange={(d) => handleChange(null, {
                name: 'invoiceDate',
                value: (d as Date).toISOString(),
              })}
            />
          </LocalizationProvider>
        </Stack>

        <Box sx={{ my: 2 }}>
          {packages.length === 0 && (
          <Box>
            <Typography gutterBottom>
              Ship an item to create new invoice.
            </Typography>
          </Box>
          )}
          {
            packages.reduce<ReactElement[]>((chipElements, pkg) => {
              (pkg.barcodeItems || [])
                .forEach((bi) => {
                  const selected = formValues.items
                    .some(
                      ({ type, value }) => (
                        type === INOVICE_ITEM_TYPES.BARCODE_ITEM
                        && value === bi
                      ),
                    );
                  const available = !invoices
                    .filter((inv) => inv !== invoice)
                    .some((inv) => inv.items.some(
                      ({ type, value }) => (
                        type === INOVICE_ITEM_TYPES.BARCODE_ITEM
                          && value === bi),
                    ));
                  const disabled = !available;
                  const color = disabled ? undefined : 'primary';
                  const variant = selected ? '' : 'outlined';
                  const icon = (selected || !available) ? <DoneIcon /> : undefined;
                  const item = {
                    type: INOVICE_ITEM_TYPES.BARCODE_ITEM,
                    value: bi,
                  };
                  chipElements.push((
                    <Chip
                      key={bi}
                      disabled={disabled}
                      sx={{ m: 0.5 }}
                      label={bi}
                      color={color}
                      variant={variant as ChipProps['variant']}
                      icon={icon}
                      onClick={() => {
                        toggleInvoiceItem(item, true);
                      }}
                    />
                  ));
                });
              return chipElements;
            }, [])
          }
        </Box>

        <Box sx={{ mb: 2 }}>
          {
            additionalCharges.map((ac) => {
              const selected = formValues.items
                .some(
                  ({ type, value }) => (
                    type === INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE
                    && value === ac._id
                  ),
                );

              const available = !invoices
                .filter((inv) => inv !== invoice)
                .some((inv) => inv.items.some(
                  ({ type, value }) => (
                    type === INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE
                      && value === ac._id),
                ));
              const disabled = !available;
              const color = disabled ? undefined : 'primary';
              const variant = selected ? '' : 'outlined';
              const icon = (selected || !available) ? <DoneIcon /> : undefined;
              const item = {
                type: INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE,
                value: ac._id,
              };
              return (
                <Chip
                  key={ac._id}
                  disabled={disabled}
                  sx={{ m: 0.5 }}
                  label={ac.name}
                  color={color}
                  variant={variant as ChipProps['variant']}
                  icon={icon}
                  onClick={() => {
                    toggleInvoiceItem(item, true);
                  }}
                />
              );
            })
          }
        </Box>

        <Box>
          <BarcodeInputBox
            onSubmit={(event, newBarcode) => {
              const barcodeValue = newBarcode.barcode as string;
              if (!packages.some((p) => (p.barcodeItems || []).includes(barcodeValue))) {
                // input barcode should be exists in packages
                return;
              }

              if (invoices.some((inv) => inv.items.some((itm) => itm.value === barcodeValue))) {
                // input barcode should not exists in other invoices
                return;
              }

              const item = {
                type: INOVICE_ITEM_TYPES.BARCODE_ITEM,
                value: barcodeValue,
              };
              toggleInvoiceItem(item, false);
              (event as { target: { value: string } }).target.value = '';
            }}
          />
        </Box>
      </FormControl>

      <Stack
        spacing={2}
        direction="row"
        sx={{ display: 'flex', justifyContent: 'flex-end' }}
      >

        <Alert
          variant="outlined"
          severity="warning"
          sx={{
            visibility: errors.items ? 'visible' : 'hidden',
            width: '470px',
            py: 0,
          }}
        >
          {errorMessages.items as ReactNode}
        </Alert>

        <div>
          <Button
            variant="outlined"
            onClick={hideModal}
          >
            Cancel
          </Button>
        </div>

        <div>
          <Button
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit}
          >
            {invoice ? 'Save' : 'Add'}
          </Button>
        </div>
      </Stack>
    </>
  );
}
